import uuid from "../../../library/uuid.js";
import global from "../../global.js";
import ObjectHandle from "../ObjectHandle.js";
const d3 = global.d3;

import {simplify} from "svg-utils";
import {format_distance} from "./Utils.js";

// const log = global.log.getLogger("f_mtac.scene.drivers.line");

const Line = function (group, color) {
    this.data = {
        id: `local-${uuid()}`,
        segments_number: 0,
        segments: [[]],
        color: color,
        class: ["node", "line", "object", "movable", "removable"],
        storage: "object"
    };
    this.scene = global.scene; // Common style.rule_set
    this.group = group;
    this.handle = null;
    this.node = null;
    this.line = null;
    this.timer = null;
    this.position = {
        x: 0,
        y: 0
    };
    this.initial_position = {
        x: 0,
        y: 0
    };
};
Line.prototype.render_driver = d3
    .line()
    .x((d) => d.x)
    .y((d) => d.y);
Line.prototype.render = function () {
    // Display the new object after having transformed the data for d3.
    const data = this.data.segments.reduce((s1, s2) => s1.concat(s2));
    return this.render_driver(simplify(data, 3 / global.scene.root_transform.k, true));
};
Line.prototype.draw = function (x, y) {
    // Create an object with origin {x, y}.
    this.group.select(`#${this.data.id}`).remove();
    this.handle = this.group.append("g").attr("class", "handle").attr("id", this.data.id);
    this.node = this.handle.append("g").attr("class", this.data.class.join(" "));
    this.node.attr("data-type", "graphics");
    this.node.attr("data-subtype", "line");
    this.data.segments[0].push({x, y});
    this.position.x = x;
    this.position.y = y;
    this.initial_position.x = x;
    this.initial_position.y = y;
    this.line = this.node.append("path").attr("stroke", this.data.color).attr("stroke-width", 3).attr("stroke-opacity", 1).attr("fill", "none").attr("d", this.render());
    // distance is refering to real distance between the two last points
    this.distance_group = this.node.append("g").attr("class", "distance_group");
    this.distance = this.distance_group.append("g").attr("class", "distance");
    this.distance_text_group = this.distance.append("g").attr("id", "distance_overlay");
    this.distance_frame = this.distance_text_group
        .append("rect")
        .attr("rx", global.app.scale.wb / 4)
        .attr("opacity", "50%")
        .attr("fill", this.data.color);
    this.distance_text = this.distance_text_group.append("text").attr("id", "distance_text");
    if (global.storage.selected_context.projection.project == null) {
        //grid scale not visible if sitac is not georeferenced
        this.scene.style.rule_set(
            "distance_group_visibility",
            `
            #${this.data.id} g.distance_group {visibility: hidden;}
        `
        );
    } else {
        this.scene.style.rule_set(
            "distance_group_visibility",
            `
        #${this.data.id} g.distance_group {visibility: visible;}
    `
        );
    }
};
Line.prototype.update = function (x, y) {
    // Add point {x, y} to the object...
    let dx = x - this.position.x,
        dy = y - this.position.y;
    // check size of segment
    if (Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2)) < 5 / global.scene.root_transform.k) {
        // ...except if it is too close from the previous one.
        return;
    }

    // add segment
    let N = this.data.segments_number;
    this.data.segments[N][1] = {x, y};
    this.line.attr("d", this.render());
    this.position.x = x;
    this.position.y = y;
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.add_segment(), 500);
    // Compute the real world radius distance
    let computed_distance = global.view.get_distance_meter(
        [this.position.x, this.position.y],
        [this.initial_position.x, this.initial_position.y]
    );

    if (computed_distance !== undefined) { // i.e. if a projection is defined for the view
        this.distance_text.text(format_distance(computed_distance));
    }

    // Calculate the middle to place the distance on it
    let middle_radius = {};
    middle_radius.x = (this.initial_position.x + this.position.x) / 2;
    middle_radius.y = (this.initial_position.y + this.position.y) / 2;
    // Fetch the dimension of the box to prepare the frame
    var {width: w, height: h, x1, y1} = this.distance_text.node().getBBox();
    // Move the text and frame a little bit to center it on the line
    x1 = middle_radius.x - w / 2;
    y1 = middle_radius.y + h / 3; // the division here work fine (tested to find this value)
    this.distance_text.attr("x", x1).attr("y", y1);
    // Padding
    h += 6;
    w += 18;
    x1 = middle_radius.x - w / 2;
    y1 = middle_radius.y - h / 2;
    this.distance_frame.attr("x", x1).attr("y", y1).attr("width", w).attr("height", h);
    // Transform-origin is necessary here for the scale on zoom and dezoom
    this.distance.attr("transform-origin", `${middle_radius.x}px ${middle_radius.y}px`);

    this.distance.attr("transform-box", "fill-box");
};
Line.prototype.add_segment = function () {
    // The computing of the geo-distance need to take into account the fact that Line is a succession of segments.
    let N = this.data.segments_number;
    this.data.segments.push([this.data.segments[N][1]]);
    this.initial_position = {x: this.data.segments[N][1].x, y: this.data.segments[N][1].y};
    this.data.segments_number = N + 1;
    this.distance = this.distance_group.append("g").attr("class", "distance");
    this.distance_text_group = this.distance.append("g").attr("id", "distance_overlay");
    this.distance_frame = this.distance_text_group
        .append("rect")
        .attr("rx", global.app.scale.wb / 4)
        .attr("opacity", "50%")
        .attr("fill", this.data.color);
    this.distance_text = this.distance_text_group.append("text").attr("id", "distance_text");
    window.clearTimeout(this.timer);
};
Line.prototype.end = function () {
    this.scene.style.rule_set(
        "distance_group_visibility",
        `
        #${this.data.id} g.distance_group {visibility: hidden;}
    `
    );
    // Add the handle at the end of the object and reset segment timer.
    if (this.timer !== null) {
        window.clearTimeout(this.timer);
        this.timer = null;
    }
    let object_handle = ObjectHandle({
        x: this.position.x,
        y: this.position.y,
        color: this.data.color
    });
    this.node.append(() => object_handle.node());
    return Promise.resolve();
};
Line.prototype.points = function (interval) {
    var line_node = this.line.node();
    var line_length = line_node.getTotalLength();
    var points_number = line_length / interval;
    return d3.range(points_number).map((d) => line_node.getPointAtLength(d * interval));
};
Line.prototype.is_empty = function () {
    let N = this.data.segments_number;
    return this.data.segments[N].length === 0;
};
Line.prototype.detach = function () {
    this.handle = null;
    this.node = null;
    this.line = null;
};
Line.prototype.export = function () {
    return this.handle;
};
export default Line;
